/**
 * Internal utilities for the slide-panel library. Not exported from index.ts.
 */
import type { AxiosError } from "axios";

/**
 * Returns true if `error` carries a displayable payload. `lodash.isEmpty`
 * treats `Error` instances as empty because `message` is a non-enumerable own
 * property — this helper covers that case so plain `throw new Error(msg)`
 * surfaces a banner instead of being silently swallowed.
 */
export function hasDisplayableError(error: unknown): boolean {
  if (error == null) return false;
  if (typeof error === "string") return error.length > 0;
  if (error instanceof Error) return true;
  if (typeof error === "object") return Object.keys(error).length > 0;
  return false;
}

/**
 * Extracts a human-readable message string from an AxiosError, a plain Error,
 * or a raw string.
 *
 * The canonical API error shape puts the message at response.data.message.
 * TlxHttpResponse.data is typed as `object` in the generated API client, so we
 * cast to access the field. We also fall through to `error.message` so that
 * non-Axios errors (e.g. `throw new Error(response.message)` for non-2xx
 * responses returned without rejecting the promise) surface their message
 * instead of the generic fallback.
 */
export function extractApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (typeof error === "string" && error) return error;
  const apiResponseData = (
    error as AxiosError<{ message?: string; data?: unknown }>
  )?.response?.data;
  const apiMessage = apiResponseData?.message;
  if (apiMessage) return apiMessage;
  // Several older endpoints (user management among them) put the human-readable
  // reason in `data.data` instead of `data.message`. Without this they fell
  // through to Axios's generic "Request failed with status code 4xx".
  if (typeof apiResponseData?.data === "string" && apiResponseData.data) {
    return apiResponseData.data;
  }
  const errorMessage = (error as { message?: unknown })?.message;
  if (typeof errorMessage === "string" && errorMessage) return errorMessage;
  return fallback;
}
